import React from 'react';
import { FaMusic } from 'react-icons/fa';
import HomeStreamEmbed from './HomeStreamEmbed.jsx';
import { useT } from '../../../contexts/LocaleContext.jsx';

const SongCover = ({ src, alt = '', className }) =>
  src ? (
    <img src={src} alt={alt} className={className} />
  ) : (
    <span className={`${className} listener-home-cover--empty`} aria-hidden="true">
      <FaMusic />
    </span>
  );

/** Diameters (px) of the concentric outlines centred on the stage's top-right corner. */
const STAGE_ARCS = [400, 660, 920, 1180];

/**
 * "Your match" hero as one stage panel (Figma 32 · Home page, stage node
 * 473:6805; ENG-877). Everything sits directly on a single red-gradient panel,
 * left to right: the current-song cover, the current-song text with the queue
 * pinned to the bottom of its column, then the match's stream with the
 * "Your match" eyebrow, the band name (the page's h1) and Watch underneath.
 *
 * The eyebrow reads "Your match · 94%" when `/home/feed` scored the match
 * (`matchScore`, 1-100) and plain "Your match" when the score is null (signed
 * out, no history, or a match not picked by score); it never shows 0% or an
 * empty percentage. The current song reads "Artist · Year" when the queue
 * carries a release year and the artist alone otherwise (ENG-729 / ENG-730).
 * The Figma "PaymentNudge (PROPOSED)" row is intentionally not rendered:
 * listeners tip through one-off Stripe Checkout and the app has no stored
 * payment source to check against.
 *
 * Nothing is layered over the stream embed: the eyebrow, title and Watch sit
 * below it, never on top of the video.
 */
const HomeHeroCard = ({
  band,
  isLive = false,
  embedUrl = null,
  currentSong = null,
  upcoming = [],
  matchScore = null,
  onWatch,
}) => {
  const t = useT();
  if (!band) return null;
  const hasScore = typeof matchScore === 'number' && Number.isFinite(matchScore) && matchScore >= 1;

  return (
    <section className="listener-home-hero" aria-labelledby="listener-home-hero-title" data-testid="home-hero">
      <span className="listener-home-hero-arcs" aria-hidden="true" data-testid="home-hero-arcs">
        {STAGE_ARCS.map((diameter) => (
          <span key={diameter} className={`listener-home-hero-arc listener-home-hero-arc--${diameter}`} />
        ))}
      </span>

      <SongCover src={currentSong?.ArtworkURL} className="listener-home-hero-cover" />

      <div className="listener-home-hero-song" data-testid="home-hero-song">
        <span className="listener-home-hero-eyebrow">{t('home.currentSong')}</span>
        {currentSong ? (
          <>
            <p className="listener-home-hero-song-title">{currentSong.Title}</p>
            {currentSong.Artist && (
              <p className="listener-home-hero-song-artist">
                {currentSong.Artist}
                {/* The year is shown as-is, never locale-formatted ("2015", not "2,015"). */}
                {currentSong.ReleaseYear ? ` · ${currentSong.ReleaseYear}` : null}
              </p>
            )}
            {currentSong.RequestedBy && (
              <p className="listener-home-hero-requester" data-testid="home-hero-requester">
                <span className="listener-home-hero-requester-label">{t('home.hero.requestedBy')}</span>
                <span className="listener-home-hero-requester-name">{currentSong.RequestedBy}</span>
              </p>
            )}
          </>
        ) : (
          <p className="listener-home-hero-song-title listener-home-hero-song-title--idle">
            {isLive ? t('home.hero.nothingOnStage') : t('home.hero.streamOffline')}
          </p>
        )}

        <div className="listener-home-queue">
          <h2 className="listener-home-queue-heading">{t('home.hero.queueHeading')}</h2>
          {upcoming.length > 0 ? (
            <ol className="listener-home-queue-list" aria-label={t('home.hero.upcomingSongs')}>
              {upcoming.map((item) => (
                <li key={item.RequestId ?? `${item.Title}-${item.Position}`} className="listener-home-queue-item">
                  <SongCover src={item.ArtworkURL} className="listener-home-queue-cover" />
                  <span className="listener-home-queue-title">{item.Title}</span>
                  <span className="listener-home-queue-artist">{item.Artist}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="listener-home-queue-empty">{t('home.hero.queueEmpty')}</p>
          )}
        </div>
      </div>

      <div className="listener-home-hero-match" data-testid="home-hero-match">
        <HomeStreamEmbed band={band} isLive={isLive} embedUrl={embedUrl} className="listener-home-hero-embed" />
        <span className="listener-home-hero-eyebrow listener-home-hero-eyebrow--match" data-testid="home-hero-match-eyebrow">
          {hasScore ? t('home.hero.eyebrowWithScore', { score: matchScore }) : t('home.hero.eyebrow')}
        </span>
        <div className="listener-home-hero-title-row">
          <h1 id="listener-home-hero-title" className="listener-home-hero-title">{band.Name}</h1>
          <button type="button" className="listener-home-hero-watch" onClick={onWatch}>
            {t('home.hero.watch')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default HomeHeroCard;
